<script setup lang="ts">
import { ref, computed, onUnmounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import StructuredAnswer from '@/ai/components/StructuredAnswer.vue';
import type { StructuredAnswer as StructuredAnswerType } from '@/ai/utils/types';
import { useAlertDetailStore } from '@/alert/stores/alert-detail';
import { useChatbotConfigStore } from '@/config/stores/chatbotConfig';
import { customAxiosInstance } from '@/common/api/utils/mutator';
import { useInternational } from '@/common/locale';

/**
 * 알람 상세 'AI 분석' 탭 — 이 알람의 근본 원인(RCA)과 조치를 AI 가 분석해 보여준다.
 * 실행 방식은 환경설정 chatbot.alarmDetailRcaMode: manual(버튼) | auto(탭 진입 시 자동).
 * (백엔드 POST /api/v9/chatbot/alarm-rca — 동일 알람은 캐시 재사용, rerun=강제 재실행)
 */
/** 풍부한 알람 맥락(발생시점·대상·심각도)을 직접 넘길 때 사용. 없으면 selectedRuleInfo 에서 추론. */
interface AlarmRcaContext {
  alertRuleId?: string;
  targetId?: string;
  /** 이벤트 알람 발생(occurrence) 고유 ID — RCA 안정 캐시 키(교차오염 방지). */
  resultId?: string;
  ruleName?: string;
  ruleType?: string;
  level?: string;
  triggerTime?: string;
  targetName?: string;
  targetKind?: string;
}
const props = defineProps<{
  isShow: boolean;
  /** 이벤트 알람 상세 등에서 넘기는 발생시점·대상 맥락. */
  context?: AlarmRcaContext | null;
  /** true 면 특정 발생시점·대상·인과를 더 정밀/명확하게 분석(이벤트 알람 상세). */
  detailed?: boolean;
  /** true 면 탭 진입 시 저장된 RCA 캐시를 자동 조회해 표시(없으면 버튼). 이벤트 발생 occurrence 는 고정이라 권장. */
  autoLoadCache?: boolean;
}>();

const { t } = useInternational();

const { selectedRuleInfo } = storeToRefs(useAlertDetailStore());
const { settings } = storeToRefs(useChatbotConfigStore());

const mode = computed<string>(() => settings.value?.alarmDetailRcaMode ?? 'manual');

const loading = ref(false);
const rca = ref<StructuredAnswerType | null>(null);
const errorMsg = ref<string | null>(null);
const ran = ref(false);
// 요청 토큰 — 응답 대기 중 다른 알람으로 전환되면 이전 요청 응답이 새 알람 화면을 덮어쓰지 않게 한다.
let reqSeq = 0;

// 진행중(pending) 분석 폴링 — 분석 중 탭/창을 닫았다 다시 열어도 백그라운드 완료분을 따라잡는다.
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let pollStartAt = 0;
const stopPolling = (): void => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};
const schedulePoll = (): void => {
  // 탭이 숨겨진 뒤 늦게 도착한 pending 응답이 폴링을 재예약하는 역경합 방지 —
  // 숨김 중엔 예약하지 않고, 재개는 isShow watch 의 재개 분기가 담당 (3차 리뷰 P2)
  if (!props.isShow) return;
  if (Date.now() - pollStartAt > POLL_TIMEOUT_MS) {
    loading.value = false;
    errorMsg.value = t('WORD.ALARM_RCA.DELAYED');
    return;
  }
  pollTimer = setTimeout(() => void callRca({ cacheOnly: true, poll: true }), POLL_INTERVAL_MS);
};

interface AlarmRcaResponse {
  status: string;
  rca: StructuredAnswerType | null;
  cached?: boolean;
  error?: string;
}

const callRca = async (
  opts: { rerun?: boolean; cacheOnly?: boolean; poll?: boolean } = {},
): Promise<void> => {
  // 폴링 콜백은 loading=true 상태에서 들어오므로 loading 가드는 사용자 액션에만 적용.
  if (loading.value && !opts.poll) return;
  const info = selectedRuleInfo.value;
  const ctx = props.context ?? null;
  // 자동/수동 공통 게이트 — 알람 상세(triggerTime) 도착 전 실행하면 불완전 키로 RCA 가
  // 생성·캐시된다 (재리뷰 P1). 자동 경로는 도착 시 context watch 가 재시도한다.
  if (contextIncomplete.value) {
    if (!opts.cacheOnly) errorMsg.value = t('WORD.ALARM_RCA.LOADING_DETAIL');
    return;
  }
  const alertRuleId = ctx?.alertRuleId ?? info?.alertRuleId;
  const targetId = ctx?.targetId ?? info?.alertTargetId;
  if (!alertRuleId && !targetId) {
    if (!opts.cacheOnly) errorMsg.value = t('WORD.ALARM_RCA.NO_ALARM_INFO');
    return;
  }
  // 폴링은 기존 요청 토큰을 유지(같은 분석 추적), 사용자 액션은 새 토큰 발급 + 폴링 윈도우 리셋.
  const mySeq = opts.poll ? reqSeq : ++reqSeq;
  if (!opts.poll) {
    stopPolling();
    pollStartAt = Date.now();
  }
  // 실행 요청(수동 버튼/auto)일 때만 즉시 로딩 표시. cacheOnly 조회는 결과 상태(pending)에 따라 결정.
  if (!opts.cacheOnly) {
    loading.value = true;
    ran.value = true;
  }
  errorMsg.value = null;
  try {
    const res = await customAxiosInstance<AlarmRcaResponse>({
      url: '/api/v9/chatbot/alarm-rca',
      method: 'POST',
      data: {
        alertRuleId,
        targetId,
        resultId: ctx?.resultId,
        ruleName: ctx?.ruleName ?? info?.alertRuleName,
        ruleType: ctx?.ruleType ?? info?.alertRuleType,
        level: ctx?.level,
        triggerTime: ctx?.triggerTime,
        targetName: ctx?.targetName,
        targetKind: ctx?.targetKind,
        detailed: props.detailed ?? false,
        rerun: opts.rerun ?? false,
        cacheOnly: opts.cacheOnly ?? false,
      },
    });
    if (mySeq !== reqSeq) return; // 응답 대기 중 다른 알람으로 전환됨 — stale 응답 무시
    const status = res.data?.status;
    if (status === 'ready' && res.data.rca) {
      stopPolling();
      rca.value = res.data.rca;
      ran.value = true; // 캐시 로드도 '분석됨' 상태 — 버튼은 '다시 분석'
      loading.value = false;
    } else if (status === 'pending') {
      // 백그라운드 분석 진행중 — 로딩 표시하고 ready 가 될 때까지 폴링.
      ran.value = true;
      loading.value = true;
      schedulePoll();
    } else if (status === 'error') {
      stopPolling();
      loading.value = false;
      // 실행 요청 또는 폴링 결과의 error 는 노출(최초 cacheOnly 자동조회의 조용한 진입은 제외).
      if (!opts.cacheOnly || opts.poll) {
        rca.value = null;
        errorMsg.value = res.data?.error || t('WORD.ALARM_RCA.FAILED');
      }
    } else {
      // none
      if (!opts.cacheOnly) {
        stopPolling();
        loading.value = false;
        rca.value = null;
        errorMsg.value = res.data?.error || t('WORD.ALARM_RCA.FETCH_FAILED');
      } else if (opts.poll) {
        // 폴링 도중 결과가 사라짐(만료 등) — 폴링 종료 + 사용자 고지.
        stopPolling();
        loading.value = false;
        rca.value = null;
        errorMsg.value = t('WORD.ALARM_RCA.RESULT_NOT_FOUND');
      }
      // 최초 cacheOnly 자동조회 + none: 조용히 '분석 실행' 버튼 유지
    }
  } catch (e) {
    if (mySeq !== reqSeq) return; // stale — 무시
    stopPolling();
    if (!opts.cacheOnly || opts.poll) {
      loading.value = false;
      const status = (e as { response?: { status?: number } })?.response?.status;
      errorMsg.value =
        status === 503 ? t('WORD.ALARM_RCA.DISABLED') : t('WORD.ALARM_RCA.REQUEST_FAILED');
    }
  }
};

/**
 * 이벤트 알람 컨텍스트가 아직 절반만 채워진 상태 — occurrence(resultId)는 즉시 오지만
 * triggerTime(eventData)은 비동기 도착. 이 상태의 실행은 잘못된 키로 RCA 가 캐시된다.
 */
const contextIncomplete = computed<boolean>(
  () => !!props.context?.resultId && !props.context?.triggerTime,
);

/** 실행 버튼 라벨 — 진행중 / 재분석(이미 1회 실행) / 최초 실행. */
const runButtonLabel = computed<string>(() => {
  if (loading.value) return t('WORD.ALARM_RCA.RUNNING');
  return ran.value ? t('WORD.ALARM_RCA.RERUN') : t('WORD.ALARM_RCA.RUN');
});

/** 분석 실행/재실행 (LLM 호출). */
const runRca = (rerun = false): Promise<void> => callRca({ rerun });
/** 저장된 RCA 캐시만 조회 — 있으면 표시, 없으면 버튼 유지(실행 안 함). */
const loadCache = (): Promise<void> => callRca({ cacheOnly: true });

/** 탭이 열려있고 아직 실행/로딩 중이 아니면 모드에 따라 자동 실행(auto) / 캐시 자동조회(autoLoadCache). */
const maybeAutoTrigger = (): void => {
  if (!props.isShow || ran.value || loading.value) return;
  // 불완전 컨텍스트면 대기 — 도착하면 context watch 가 다시 트리거 (callRca 내부 게이트와 동일 기준)
  if (contextIncomplete.value) return;
  if (mode.value === 'auto') void runRca(false);
  else if (props.autoLoadCache) void loadCache();
};

// 새 알람/발생시점으로 전환되면: 진행중 요청 무효화 + 이전 결과 초기화 후, 탭이 열려있으면 재실행/재조회.
// (탭 패널이 슬라이드 내에서 재사용되어 isShow 가 계속 true 인 경우에도 새 알람을 다시 분석하도록.)
watch(
  () => [
    props.context?.resultId, // 발생 occurrence 우선 — 알람 전환 감지의 안정 키
    props.context?.alertRuleId ?? selectedRuleInfo.value?.alertRuleId,
    props.context?.targetId ?? selectedRuleInfo.value?.alertTargetId,
    props.context?.triggerTime,
  ],
  () => {
    reqSeq += 1; // 진행중 요청 무효화 — stale 응답이 새 알람 결과를 덮어쓰지 않게
    stopPolling(); // 이전 알람의 진행중 폴링 중단
    rca.value = null;
    errorMsg.value = null;
    ran.value = false;
    loading.value = false;
    maybeAutoTrigger();
  },
);

// 탭 진입 시: auto 모드 → 자동 실행(캐시 우선). 그 외 autoLoadCache → 저장된 캐시를 자동 표시(없으면 버튼).
watch(
  () => props.isShow,
  (show) => {
    if (!show) {
      stopPolling(); // 패널이 닫히면 진행중 폴링 중단(불필요 요청 방지)
      return;
    }
    // pending 상태로 숨겼다 다시 열면 ran/loading=true 라 maybeAutoTrigger 가 막혀
    // 폴링이 영구히 재개되지 않는다 (3차 리뷰 P2) — 진행중 표시가 남아있으면 즉시 재개.
    // 숨겨져 있던 시간이 타임아웃을 소진하지 않도록 윈도우도 리셋.
    if (loading.value && !pollTimer) {
      pollStartAt = Date.now();
      void callRca({ cacheOnly: true, poll: true });
      return;
    }
    maybeAutoTrigger();
  },
  { immediate: true },
);

// 컴포넌트 해제 시 진행중 폴링 정리(타이머 누수 방지).
onUnmounted(() => stopPolling());
</script>

<template>
  <div class="alert-ai-analysis">
    <div class="alert-ai-analysis__head">
      <span class="alert-ai-analysis__title">{{ t('WORD.ALARM_RCA.TITLE') }}</span>
      <button
        v-if="mode !== 'auto' || rca || errorMsg"
        type="button"
        class="alert-ai-analysis__btn"
        :disabled="loading || contextIncomplete"
        @click="runRca(ran)"
      >
        {{ runButtonLabel }}
      </button>
    </div>

    <div
      v-if="loading"
      class="alert-ai-analysis__loading"
      role="status"
      aria-live="polite"
    >
      <div
        class="alert-ai-analysis__radar"
        aria-hidden="true"
      >
        <span class="alert-ai-analysis__radar-sweep" />
        <span class="alert-ai-analysis__radar-ring" />
        <span class="alert-ai-analysis__radar-ring alert-ai-analysis__radar-ring--delay" />
        <span class="alert-ai-analysis__radar-core" />
      </div>
      <div class="alert-ai-analysis__loading-title">
        {{ t('WORD.ALARM_RCA.LOADING_TITLE') }}
      </div>
      <ul class="alert-ai-analysis__steps">
        <li
          class="alert-ai-analysis__step"
          style="--i: 0"
        >
          <span class="alert-ai-analysis__step-dot" />{{ t('WORD.ALARM_RCA.STEP_COLLECT') }}
        </li>
        <li
          class="alert-ai-analysis__step"
          style="--i: 1"
        >
          <span class="alert-ai-analysis__step-dot" />{{ t('WORD.ALARM_RCA.STEP_PATTERN') }}
        </li>
        <li
          class="alert-ai-analysis__step"
          style="--i: 2"
        >
          <span class="alert-ai-analysis__step-dot" />{{ t('WORD.ALARM_RCA.STEP_CAUSALITY') }}
        </li>
        <li
          class="alert-ai-analysis__step"
          style="--i: 3"
        >
          <span class="alert-ai-analysis__step-dot" />{{ t('WORD.ALARM_RCA.STEP_ACTION') }}
        </li>
      </ul>
      <div
        class="alert-ai-analysis__bar"
        aria-hidden="true"
      >
        <span />
      </div>
    </div>
    <structured-answer
      v-else-if="rca"
      :answer="rca"
      class="alert-ai-analysis__result"
    />
    <div
      v-else-if="errorMsg"
      class="alert-ai-analysis__state alert-ai-analysis__state--error"
    >
      {{ errorMsg }}
    </div>
    <div
      v-else
      class="alert-ai-analysis__state"
    >
      {{ t('WORD.ALARM_RCA.EMPTY_GUIDE', { action: t('WORD.ALARM_RCA.RUN') }) }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.alert-ai-analysis {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  padding: 12px;
  overflow: auto;

  &__head {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: var(--main-font-color, #1f2329);
  }

  &__btn {
    padding: 5px 14px;
    cursor: pointer;
    border: 1px solid var(--color-primary, #4a7dff);
    border-radius: 6px;
    background: var(--color-primary, #4a7dff);
    font-size: 12px;
    color: #ffffff;
    &:hover:not(:disabled) {
      opacity: 0.88;
    }
    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  &__state {
    padding: 24px 12px;
    border: 1px dashed var(--config-group-box-border-color, #e0e0e0);
    border-radius: 8px;
    font-size: 13px;
    color: var(--sub-font-color, #666666);
    text-align: center;

    &--error {
      border-style: solid;
      border-color: var(--color-danger, #e5484d);
      color: var(--color-danger, #e5484d);
    }
  }

  &__result {
    margin-top: 0;
  }

  /* ─── 분석 중 애니메이션 (레이더 스캔 + 단계 진행) ─── */
  &__loading {
    display: flex;
    flex-direction: column;
    gap: 14px;
    align-items: center;
    padding: 28px 16px 22px;
    border: 1px dashed var(--config-group-box-border-color, #e0e0e0);
    border-radius: 8px;
  }

  &__radar {
    position: relative;
    width: 56px;
    height: 56px;
  }

  &__radar-core {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 14px;
    height: 14px;
    margin: -7px 0 0 -7px;
    border-radius: 50%;
    background: var(--color-primary, #4a7dff);
    animation: aia-core 1.4s ease-in-out infinite;
  }

  &__radar-ring {
    position: absolute;
    opacity: 0;
    border: 2px solid var(--color-primary, #4a7dff);
    border-radius: 50%;
    animation: aia-ring 1.8s ease-out infinite;
    inset: 0;

    &--delay {
      animation-delay: 0.9s;
    }
  }

  &__radar-sweep {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: conic-gradient(
      from 0deg,
      rgb(74, 125, 255, 0) 0deg,
      rgb(74, 125, 255, 0.28) 70deg,
      rgb(74, 125, 255, 0) 100deg
    );
    animation: aia-sweep 1.6s linear infinite;
  }

  &__loading-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--main-font-color, #1f2329);
  }

  &__steps {
    display: flex;
    flex-direction: column;
    gap: 7px;
    width: 100%;
    max-width: 260px;
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__step {
    display: flex;
    gap: 8px;
    align-items: center;
    opacity: 0.45;
    font-size: 12px;
    color: var(--sub-font-color, #8a9099);
    animation: aia-step 3.6s ease-in-out infinite;
    animation-delay: calc(var(--i) * 0.9s);
  }

  &__step-dot {
    flex-shrink: 0;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentcolor;
  }

  &__bar {
    position: relative;
    width: 100%;
    max-width: 260px;
    height: 3px;
    overflow: hidden;
    border-radius: 3px;
    background: var(--color-gray-08-01, #eef0f3);

    span {
      position: absolute;
      top: 0;
      left: -40%;
      width: 40%;
      height: 100%;
      border-radius: 3px;
      background: var(--color-primary, #4a7dff);
      animation: aia-bar 1.5s ease-in-out infinite;
    }
  }

  @keyframes aia-core {
    0%,
    100% {
      opacity: 0.7;
      transform: scale(0.8);
    }
    50% {
      opacity: 1;
      transform: scale(1.15);
    }
  }

  @keyframes aia-ring {
    0% {
      opacity: 0.55;
      transform: scale(0.45);
    }
    100% {
      opacity: 0;
      transform: scale(1);
    }
  }

  @keyframes aia-sweep {
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes aia-step {
    0%,
    100% {
      opacity: 0.4;
      color: var(--sub-font-color, #8a9099);
    }
    8%,
    18% {
      opacity: 1;
      color: var(--color-primary, #4a7dff);
    }
  }

  @keyframes aia-bar {
    0% {
      left: -40%;
    }
    100% {
      left: 100%;
    }
  }
}
</style>
